import type { Application, Container } from 'pixi.js';
import type { BaseScene } from '../scenes/BaseScene';
import type { SceneConstructor, SceneContext } from '../types/game.types';
import { logger } from '../utils/logger';

export class SceneManager {
  private app: Application;
  private currentScene: BaseScene | null = null;
  private sceneContext: SceneContext;
  private isTransitioning: boolean = false;

  constructor(app: Application) {
    this.app = app;
    this.sceneContext = {
      app,
      width: app.screen.width,
      height: app.screen.height,
    };
  }

  /**
   * Transition to a new scene with optional fade effect.
   */
  async changeScene(SceneClass: SceneConstructor, useFade: boolean = true): Promise<void> {
    if (this.isTransitioning) {
      logger.warn('Scene transition already in progress');
      return;
    }

    this.isTransitioning = true;

    try {
      // Fade out current scene
      if (this.currentScene && useFade) {
        await this.fadeOut(this.currentScene);
      }

      // Exit current scene
      if (this.currentScene) {
        logger.info('Exiting scene:', this.currentScene.constructor.name);
        this.currentScene.exit();
        this.app.stage.removeChild(this.currentScene);
      }

      // Create and load new scene
      const newScene = new SceneClass(this.sceneContext);
      logger.info('Loading scene:', newScene.constructor.name);

      if (!newScene.getIsLoaded()) {
        await newScene.load();
        newScene['setLoaded'](true);
      }

      // Enter new scene
      this.app.stage.addChild(newScene);
      newScene.enter();
      this.currentScene = newScene;

      // Fade in new scene
      if (useFade) {
        await this.fadeIn(newScene);
      }

      logger.info('Scene transition complete:', newScene.constructor.name);
    } catch (error) {
      logger.error('Scene transition failed:', error);
      throw error;
    } finally {
      this.isTransitioning = false;
    }
  }

  /**
   * Update the current scene.
   */
  update(delta: number): void {
    if (this.currentScene) {
      this.currentScene.update(delta);
    }
  }

  /**
   * Handle window resize events.
   */
  resize(width: number, height: number): void {
    this.sceneContext.width = width;
    this.sceneContext.height = height;

    if (this.currentScene) {
      this.currentScene.resize(width, height);
    }
  }

  /**
   * Fade out a scene over 300ms.
   */
  private fadeOut(scene: Container): Promise<void> {
    return new Promise((resolve) => {
      const duration = 300;
      const startTime = Date.now();
      const startAlpha = scene.alpha;

      const animate = (): void => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);

        scene.alpha = startAlpha * (1 - progress);

        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          resolve();
        }
      };

      animate();
    });
  }

  /**
   * Fade in a scene over 300ms.
   */
  private fadeIn(scene: Container): Promise<void> {
    return new Promise((resolve) => {
      const duration = 300;
      const startTime = Date.now();

      scene.alpha = 0;

      const animate = (): void => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);

        scene.alpha = progress;

        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          resolve();
        }
      };

      animate();
    });
  }

  /**
   * Get the current active scene.
   */
  getCurrentScene(): BaseScene | null {
    return this.currentScene;
  }

  /**
   * Check if a scene transition is in progress.
   */
  getIsTransitioning(): boolean {
    return this.isTransitioning;
  }
}
